// src/pages/public/PublicLayout.jsx
import React, { useEffect, useState } from "react";
import { getBagCount, BAG_CHANGED_EVENT } from "../../state/bagStore";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../../hooks/useSession";
import { useCountry } from "../../hooks/useCountry";
import {
  getAvailableCountries,
  AVAILABLE_COUNTRIES_CHANGED_EVENT,
} from "../../state/availableCountriesStore";
import PublicFooter from "../../components/public/PublicFooter";

const COUNTRY_LABELS = {
  NZ: "New Zealand",
  AU: "Australia",
  GB: "United Kingdom",
  US: "United States",
};

function getCountryLabel(country) {
  return COUNTRY_LABELS[country] || country || "your region";
}

export default function PublicLayout({
  children,
  onOpenGuide,
}) {
  const location = useLocation();
  const {
    isAuthenticated = false,
    isShopper = false,
    isMerchant = false,
    logout,
  } = useSession();

  const isMerchantDashboard =
    location.pathname.startsWith("/merchant/dashboard") ||
    location.pathname.startsWith("/merchant/settings") ||
    location.pathname === "/merchant";
  const isLandingPage = location.pathname === "/";
  const [bagCount, setBagCount] = useState(getBagCount());
  const { country, setCountry } = useCountry();
  const [showRegionPrompt, setShowRegionPrompt] = useState(false);
  const [availableCountries, setAvailableCountriesState] = useState(
    getAvailableCountries()
  );
  const [regionOpen, setRegionOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const availableCountryOptions = availableCountries.map((code) => ({
    code,
    label: getCountryLabel(code),
  }));

  useEffect(() => {
    const onBagChanged = () => setBagCount(getBagCount());
    window.addEventListener(BAG_CHANGED_EVENT, onBagChanged);
    return () => window.removeEventListener(BAG_CHANGED_EVENT, onBagChanged);
  }, []);

  let navItems = [];

  if (isAuthenticated && isMerchant) {
    navItems = [
      { to: "/merchant/dashboard/catalog", label: "Catalog Manager" },
      { to: "/merchant", label: "Veya Insights" },
      { to: "/merchant/settings", label: "Settings" },
    ];
  } else if (isAuthenticated && isShopper) {
    navItems = [
      { to: "/edits", label: "My Edits" },
      { to: "/moods", label: "Veya's Edits" },
      { to: "/browse", label: "Browse" },
      { to: "/account", label: "Account" },
    ];
  } else {
    navItems = [
      { to: "/moods", label: "Veya's Edits" },
      { to: "/browse", label: "Browse" },
      { to: "/login", label: "Sign in" },
    ];
  }

  const showLogout = isAuthenticated && (isShopper || isMerchant);

  const handleLogout = () => {
    if (typeof logout === "function") {
      logout();
    }
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setRegionOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onAvailableCountriesChanged = (event) => {
      setAvailableCountriesState(event.detail || ["NZ"]);
    };

    window.addEventListener(
      AVAILABLE_COUNTRIES_CHANGED_EVENT,
      onAvailableCountriesChanged
    );

    return () =>
      window.removeEventListener(
        AVAILABLE_COUNTRIES_CHANGED_EVENT,
        onAvailableCountriesChanged
      );
  }, []);

  useEffect(() => {
    if (!country) return;

    if (!availableCountries.includes(country)) {
      setShowRegionPrompt(true);
    }
  }, [country, availableCountries]);

  return (
    <div
      className={`public-shell ${
        isMerchantDashboard ? "public-shell--merchant" : ""
      }`}
    >
      {!isMerchantDashboard && (
        <div className="public-preview-bar">
          Veya is in early development — some features and try-on results are still
          being refined.
        </div>
      )}

      {showRegionPrompt && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(43, 25, 30, 0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              borderRadius: 26,
              padding: 24,
              background: "rgba(251,247,242,0.96)",
              border: "1px solid rgba(255,255,255,0.72)",
              boxShadow: "0 24px 70px rgba(0,0,0,0.18)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                opacity: 0.62,
                marginBottom: 8,
              }}
            >
              Region
            </div>

            <h2 style={{ margin: "0 0 10px", fontSize: 24 }}>
              You’re browsing {getCountryLabel(country)} boutiques.
            </h2>

            <p style={{ margin: "0 0 18px", lineHeight: 1.6, opacity: 0.78 }}>
              Veya currently has boutiques available in New Zealand. Switch
              region to view available stores.
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowRegionPrompt(false)}
              >
                Stay on {getCountryLabel(country)}
              </button>

              <button
                type="button"
                className="public-cta"
                onClick={() => {
                  setCountry("NZ");
                  setShowRegionPrompt(false);
                }}
              >
                Switch to New Zealand
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLandingPage && (
        <header
          className={`public-header ${
            isMerchantDashboard ? "public-header--merchant" : ""
          }`}
        >
          <Link to="/" className="public-brand" style={{ textDecoration: "none" }}>
            <div className="public-wordmark">veya</div>
          </Link>

          <nav className="public-nav public-nav--desktop">
            {navItems.map((item) => (
              <Link key={item.to} className="public-navlink" to={item.to}>
                {item.label}
              </Link>
            ))}

            {showLogout && (
              <Link to="/" className="public-navlink" onClick={handleLogout}>
                Log out
              </Link>
            )}

            <Link className="public-cta" to="/fitting-room">
              Enter the Fitting Room
            </Link>

            <Link
              to="/bag"
              className="public-bag-btn"
              aria-label={`Bag${bagCount ? ` with ${bagCount} items` : ""}`}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M4 6h2l1.2 6.2a1 1 0 0 0 1 .8h7.9a1 1 0 0 0 1-.76L18.4 8H7.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="10" cy="18" r="1.4" fill="currentColor" />
                <circle cx="17" cy="18" r="1.4" fill="currentColor" />
              </svg>

              {bagCount > 0 && (
                <span className="public-bag-count">{bagCount}</span>
              )}
            </Link>
          </nav>

          <div className="public-mobile-actions">
            <Link
              to="/bag"
              className="public-bag-btn"
              aria-label={`Bag${bagCount ? ` with ${bagCount} items` : ""}`}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M4 6h2l1.2 6.2a1 1 0 0 0 1 .8h7.9a1 1 0 0 0 1-.76L18.4 8H7.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="10" cy="18" r="1.4" fill="currentColor" />
                <circle cx="17" cy="18" r="1.4" fill="currentColor" />
              </svg>

              {bagCount > 0 && (
                <span className="public-bag-count">{bagCount}</span>
              )}
            </Link>

            <button
              type="button"
              className="public-menu-btn"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-expanded={mobileMenuOpen}
            >
              Menu
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="public-mobile-menu">
              <div className="veya-menu-heading">Explore</div>

              {navItems.map((item) => (
                <Link key={item.to} className="public-mobile-link" to={item.to}>
                  {item.label}
                </Link>
              ))}

              <div className="veya-menu-sep" />

              {showLogout && (
                <Link to="/" className="public-mobile-link" onClick={handleLogout}>
                  Log out
                </Link>
              )}

              <Link className="public-mobile-cta" to="/fitting-room">
                Enter the Fitting Room
              </Link>

              <div className="veya-menu-sep" />
              <div className="veya-menu-heading">Region</div>

              {!isMerchantDashboard && (
                <div className="public-mobile-region">
                  <div className="public-mobile-region-label">Browsing</div>

                  {availableCountryOptions.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      className={`public-mobile-region-option ${
                        c.code === country ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setCountry(c.code);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </header>
      )}

      {!isLandingPage && !isMerchantDashboard && (
        <div className="public-region-floating">
          <button
            type="button"
            onClick={() => setRegionOpen((v) => !v)}
            className="ghost-btn"
            style={{
              borderRadius: 999,
              padding: "10px 14px",
              background: "rgba(251,247,242,0.92)",
              boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
            }}
          >
            Browsing: {getCountryLabel(country)} ▾
          </button>

          {regionOpen && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: 0,
                borderRadius: 14,
                background: "rgba(251,247,242,0.96)",
                border: "1px solid rgba(43,43,43,0.12)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
                overflow: "hidden",
                minWidth: 180,
              }}
            >
              {availableCountryOptions.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    setCountry(c.code);
                    setRegionOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <main className="public-main">{children}</main>

      {!isLandingPage && !isMerchantDashboard && <PublicFooter onOpenGuide={onOpenGuide} />}
    </div>
  );
}